<template>
  <div id="roadPane" :style="styleObject">

    <a-tabs size="small" type="line" :animated="false" @change="onTabChange">
      <a-tab-pane key="RST" tab="路面状况">
      </a-tab-pane>
      <a-tab-pane key="HYD" tab="积水深度(mm)">
      </a-tab-pane>
      <a-tab-pane key="SDP" tab="路面雪深(cm)">
      </a-tab-pane>
      <a-tab-pane key="VIS" tab="能见度(km)">
      </a-tab-pane>
      <a-tab-pane key="SKT" tab="路面温度(℃)">
      </a-tab-pane>
    </a-tabs>
    <color-bar :colors="colors" />
    <slider 
      :max="max" 
      :min="min"
      :step="step"
      :marks="marks"
      :list="stepList"
      @slider-step="onSliderChange"
      @play-step="onSliderChange"
      @play-stopped="onSliderEnd"
    />

  </div>
</template>

<script>
import axios from 'axios'
import { computed } from 'vue'
import { useStore } from 'vuex'
import api from '../modules/api'
import BezierEasing from 'bezier-easing'
import * as PIXI from 'pixi.js'
import L from '../modules/leaflet'
import { COLORS_DEF, getColorUniforms } from '../modules/colors'
import Slider from '../components/control/Slider.vue'
import ColorBar from '../components/control/ColorBar.vue'

const PANE_ID = "road"
const PANE_LAYER = "road-layer"

const ROAD_POINTS_LENGTH = 31533;

const vertexShaderSource = `

precision highp float;
attribute vec2 aVertexPosition;
attribute vec2 aStatus;

uniform mat3 translationMatrix;
uniform mat3 projectionMatrix;

uniform float uColorStep;
uniform float uColorMin;
uniform vec3 uColorValues[100];
uniform float uColorLength;

uniform float uSize;

varying vec3 vColor;
varying float vSize; // The length of the edge of the square containing the shape


void main() {
    //gl_PointSize = 10.0;
    vSize = 2.0 * sqrt(uSize / 3.14159);

    highp float status = (aStatus.x * 256.0 + aStatus.y);
    status = status - step(32767.0, status) * 65536.0;

    highp int index = int(floor((status - uColorMin) / uColorStep));
    vColor = uColorValues[index];

    gl_PointSize = vSize + 1.0;

    //gl_PointSize = gl_PointSize * (aStatus.x * 256.0 + aStatus.y) * 0.1 + 1.0;
    gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
}
`;

const fragmentShaderSource = `
precision highp float;
varying vec3 vColor;
varying float vSize;

void main() {
  float distance = length(2.0 * gl_PointCoord - 1.0);

  gl_FragColor = vec4(vColor, 0.8);
  gl_FragColor.a = gl_FragColor.a * (1.0 - smoothstep(
      vSize - 3.0,
      vSize,
      distance * vSize
  ));

  gl_FragColor.a = gl_FragColor.a * step(10.0, vSize) + 0.8 * step(vSize, 10.0);

  gl_FragColor.r = gl_FragColor.r * step(distance, 1.0);
  gl_FragColor.g = gl_FragColor.g * step(distance, 1.0);
  gl_FragColor.b = gl_FragColor.b * step(distance, 1.0);
  gl_FragColor.a = gl_FragColor.a * step(distance, 1.0);

}
`;

function getUSize(zoom) {
  const size = [1, 2, 5, 10, 20, 80, 160, 320, 640, 1280, 2000]

  if (zoom < 3) {
    return 1
  } else if (zoom > 13) {
    return 2000
  }
  return size[zoom - 3]

}

function createShader(varType) {
  const uniforms = getColorUniforms(varType)

  uniforms.uSize = 10;
  uniforms.uTargetSize = 100;
  uniforms.uCurrentSize = 100;

  console.log(uniforms)
  return PIXI.Shader.from(vertexShaderSource, fragmentShaderSource, uniforms)
}

const easing = BezierEasing(0, 0, 0.25, 1);

export default {
  setup() {
    const store = useStore()
    const currentPaneId = computed(() => store.state.currentPaneId)
    const map = computed(() => store.state.map)
    return {
      currentPaneId,
      map
    }
  },

  components: {
    Slider,
    ColorBar,
  },

  data() {
    return {
      stepList: [],
      roadLayer: null,
      varType: "RST",
      min: 0,
      max: 20,
      step: 1,
      marks: {
        0: "00:00",
        10: "01:00",
        20: "02:00"
      },
      colors: COLORS_DEF.RST,
      pointLoaded: false,
      points: new Float32Array(),
      values: null
    }
  },

  computed: {
    styleObject() {
      return this.currentPaneId != PANE_ID ? { "display": "none" } : {}
    }
  },

  mounted() {
    this.init()
  },

  beforeUnmount() {
    this.clear()
  },

  methods: {

    onTabChange(value) {
      this.colors = COLORS_DEF[value]
      this.varType = value
      this.init();
    },

    onSliderChange(value) {
      this.setStep(value)
    },

    onSliderEnd() {
      this.setStep(this.$data.min)
    },

    getMap() {
      return this.$store.state.map
    },

    setStep(index) {
      if (this.currentPaneId == PANE_ID) {
        const mesh = this.roadLayer.utils.getContainer().children[0]

        if (mesh) {
          const refreshed = this.values.slice(
            index * ROAD_POINTS_LENGTH, (index+1) * ROAD_POINTS_LENGTH)
          mesh.geometry.getBuffer('aStatus').update(refreshed)
        }
      }
    },

    createPixiOverlay(points, statuses) {
      const doubleBuffering = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      let zoomChangeTs = null;
      var pointsMesh;

      const roadLayer = L.pixiOverlay((utils, event) => {
        var zoom = utils.getMap().getZoom();
        var container = utils.getContainer();
        var renderer = utils.getRenderer();
        var project = utils.latLngToLayerPoint;

        if (event.type === 'add') {
          const positions = []
          const status = []

          for(let i = 0; i < points.length; i = i + 2) {
            var coords = project([points[i], points[i+1]])
            positions.push(coords.x)
            positions.push(coords.y)
            status.push(1)
          }

          let geometry = new PIXI.Geometry()
          geometry.addAttribute('aVertexPosition', positions, 2)
          geometry.addAttribute('aStatus', statuses.buffer, 2, false, PIXI.TYPES.UNSIGNED_BYTE)

          pointsMesh = new PIXI.Mesh(geometry, createShader(this.varType), null, PIXI.DRAW_MODES.POINTS)
          pointsMesh.shader.uniforms.uTargetSize = getUSize(zoom)
          container.addChild(pointsMesh);
          window.pointsMesh = pointsMesh
          this.$store.dispatch('setLoading', { "loading": false })
        }

        if (event.type === 'zoomanim') {
          zoomChangeTs = 0;
          pointsMesh.shader.uniforms.uCurrentSize = pointsMesh.shader.uniforms.uSize;
          pointsMesh.shader.uniforms.uTargetSize = getUSize(event.zoom)

          return;
        }

        if (event.type === 'redraw') {
          var delta = event.delta;
          if (zoomChangeTs !== null) {
            var duration = 40;
            zoomChangeTs += delta;
            var lambda = zoomChangeTs / duration;
            if (lambda > 1) {
              lambda = 1;
              zoomChangeTs = null;
            }
            lambda = easing(lambda);
            pointsMesh.shader.uniforms.uSize = pointsMesh.shader.uniforms.uCurrentSize +
              lambda * (pointsMesh.shader.uniforms.uTargetSize - pointsMesh.shader.uniforms.uCurrentSize);
          } else {
            pointsMesh.shader.uniforms.uSize = pointsMesh.shader.uniforms.uTargetSize;
          }
        }

        renderer.render(container);

      }, new PIXI.Container(), {
        doubleBuffering: doubleBuffering,
        destroyInteractionManager: true
      })

      var ticker = new PIXI.Ticker();
      ticker.add(function(delta) {
        roadLayer.redraw({type: 'redraw', delta: delta});
      });
      ticker.start();

      return roadLayer
    },

    preparePoints() {
      return new Promise((resolve) => {
        if (!this.pointLoaded) {
          return axios.get("data/points.raw", {
            responseType:'arraybuffer'
          }).then(res => {
            this.points = new Float32Array(res.data);
            this.pointLoaded = true;
            resolve()
          });
        }
        return resolve()
      })
    },

    initRoadLayer(initValues) {
      const roadLayer = this.createPixiOverlay(this.points, initValues)
      const map = this.$store.state.map
      this.roadLayer = roadLayer
      window.roadLayer = roadLayer
      map.replaceLayer(PANE_LAYER, roadLayer)
      map.bindZoomAnim(PANE_LAYER)
    },

    init() {
      this.$store.dispatch('setLoading', { "loading": true })
      api.fetchRoadStatus2(this.varType).then((roads) => {
        window.roads = roads
        this.preparePoints().then(() => {          
          const length = roads.steps.length

          this.stepList = roads.steps;
          this.values = roads.data

          this.initRoadLayer(this.values.slice(0, ROAD_POINTS_LENGTH))

          // set parameters for slider
          this.$data.max = length - 1
          const marks = {}

          for(let i = 0; i <= length - 1; i = i + 1) {
            marks[i] = {
              label: i == 0 || i == length - 1 ? roads.steps[i].display.split(' ')[1]: "'"
            }
          }
          this.$data.marks = marks
        })
      });
    },

    clear() {
      console.log("    > closing pane .....", PANE_ID)
      const map = this.$store.state.map
      const layer = map.getLayer(PANE_LAYER);
      if (layer) {
        layer.utils.getContainer().removeChildren()
      }
      map.unbindZoomAnim(PANE_LAYER)
      map.removeLayer(PANE_LAYER)
      this.roadLayer = null;
    }
  }
}
</script>

<style>
#roadPane {
  z-index: 31; 
  width: 650px; 
  font-size: 10px;
  background-color: #fff!important;
  border-radius: .25rem!important;
  position: absolute!important;
  left: 50%!important;
  transform: translateX(-50%)!important;
  box-shadow: 0 1rem 3rem rgba(0,0,0,.175)!important;
  bottom:0;
  margin-bottom: .5rem!important;
  font-size: 10px;
}

#roadPane .ant-tabs-nav .ant-tabs-tab {
  margin-right: 3px;
}

@media (max-width: 780px) {
  #roadPane { width: 350px; }
}

</style>